/**
 * CustomerPortal
 *
 * Entry point for non-Component6 users. Checks if their organisation has been
 * onboarded (GET /customer/me). If yes → renders the customer portal. If not
 * → sends them through Setup.
 *
 * Wraps all customer routes in CustomerLayout with the company name in the header.
 */

import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { useApiClient, customerService, TenantRecord } from '../../services/apiService';
import CustomerLayout from '../../components/CustomerLayout';
import { TenantProvider } from '../../context/TenantContext';
import CustomerSetup from './CustomerSetup';
import CustomerDashboard from './CustomerDashboard';
import CustomerSettings from './CustomerSettings';
import CustomerLicenses from './CustomerLicenses';
import UserManagement from './UserManagement';
import CustomerROI from './CustomerROI';
import TeamMembers from './TeamMembers';

export default function CustomerPortal() {
  useApiClient();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<TenantRecord | null>(null);
  const [notOnboarded, setNotOnboarded] = useState(false);

  const handleOnboardComplete = useCallback(() => {
    setLoading(true);
    setNotOnboarded(false);
    customerService.getMe()
      .then((t) => { setTenant(t); navigate('/customer/dashboard'); })
      .catch(() => { setNotOnboarded(true); })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    customerService.getMe()
      .then((t) => setTenant(t))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) {
          // Tenant not registered — show onboarding wizard
          setNotOnboarded(true);
          navigate('/customer/setup');
        } else {
          // Network error, API failure — retry once before giving up
          console.error('Failed to check tenant status:', err.message || err);
          setTimeout(() => {
            customerService.getMe()
              .then((t) => { setTenant(t); setLoading(false); })
              .catch((retryErr) => {
                const retryStatus = retryErr?.response?.status;
                if (retryStatus === 404) {
                  setNotOnboarded(true);
                  navigate('/customer/setup');
                } else {
                  // Genuine failure — still show portal, it'll show errors in individual pages
                  setNotOnboarded(true);
                  navigate('/customer/setup');
                }
                setLoading(false);
              });
          }, 2000);
          return; // Don't call setLoading(false) yet — retry in progress
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spinner label="Loading your workspace..." />
      </div>
    );
  }

  if (notOnboarded) {
    // Setup page renders its own full-page layout (no nav)
    return (
      <Routes>
        <Route path="setup" element={<CustomerSetup onComplete={handleOnboardComplete} />} />
        <Route path="*" element={<Navigate to="/customer/setup" replace />} />
      </Routes>
    );
  }

  return (
    <TenantProvider>
      <CustomerLayout companyName={tenant?.tenant_name}>
        <Routes>
          <Route path="dashboard" element={<CustomerDashboard />} />
          <Route path="settings" element={<CustomerSettings />} />
          <Route path="licenses" element={<CustomerLicenses />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="team-members" element={<TeamMembers />} />
          <Route path="billing" element={<Navigate to="/customer/licenses" replace />} />
          <Route path="roi" element={<CustomerROI />} />
          <Route path="*" element={<Navigate to="/customer/dashboard" replace />} />
        </Routes>
      </CustomerLayout>
    </TenantProvider>
  );
}
